import { ofType, StateObservable } from 'redux-observable';
import { catchError, from, mergeMap, of, throwError } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { Observable } from 'rxjs/internal/Observable';
import { handleTracking } from '../helpers/handelTracking';
import { UserActions } from './UserActions';
import {
	IToastState,
	ITrackingEvent,
	TrackingEventStatus,
	TrackingEventType
} from '../common/view-model';
import { AppActions } from './AppActions';
import {
	getCookieValue,
	getIirisCookie,
	getLocalStorageValue,
	IirisIdentityKey,
	removeAllAuthenticationKeysFromLocalStorage,
	setLocalStorageValue
} from '../helpers/authenticationHelper';
import { lastUrlLocalStorageKey, allQuestionsIds } from '../common/constants';
import { GET_PROFILE } from '../graphql/queries/profile';
import {
	ADD_FAVORITE,
	CREATE_PROFILE,
	DELETE_ALL_FAVORITE,
	DELETE_FAVORITE,
	UPDATE_PROFILE
} from '../graphql/mutations/profile';
import { apolloClient } from '../graphql/ApolloClient';
import { GET_ONBOARDING_BRAND } from '../graphql/queries/onboardingBrand';
import { UPDATE_BRAND } from '../graphql/mutations/brand';
import { GET_ONBOARDING_PRODUCTS } from '../graphql/queries/onboardingProducts';
import { UPDATE_PRODUCT } from '../graphql/mutations/product';
import { buildUpdateObjectBrand, buildUpdateProduct } from '../helpers/buildUpdateObject';
import { GET_ROLES } from '../graphql/queries/getRoles';
import { RootState } from './AppReducer';
import { v4 as uuidv4 } from 'uuid';

export class UserEpics {
	static init() {
		return [
			UserEpics.getUserProfileEpic,
			UserEpics.getUserProfileBeaconEpic,
			UserEpics.getBrandInfo,
			UserEpics.saveUserIngredientsTemplateIdEpic,
			UserEpics.updateUserFavoriteProductsEpic,
			UserEpics.updateUserCompanyDetailsEpic,
			UserEpics.downloadFavoriteProductsEpic,
			UserEpics.createUserProfileBeacon,
			UserEpics.setCreateUserProfileErrorEpic,
			UserEpics.getAnonymousUserTokenEpic,
			UserEpics.registerUserClickEventEpic,
			UserEpics.updateUserUnsubscribe,
			UserEpics.updateUserSubscribe,
			UserEpics.getProductsInfo,
			UserEpics.getUserProfileBeaconLogin,
			UserEpics.updateBrandInfo,
			UserEpics.updateProductInfo,
			UserEpics.getAdminRole,
			UserEpics.removeAllFavoriteProducts,
			UserEpics.getAudienceRegistration
		];
	}

	static updateUserSubscribe = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_USER_SUBSCRIBE),
			mergeMap(_action => {
				const { payload } = _action;
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const request = {
					method: 'PUT',
					url: `${import.meta.env.VITE_BEACON_API_URL}/profile/subscribe-newsletter/${
						payload.email
					}`,
					headers: {
						'Content-Type': 'application/json',
						Authorization: getIirisCookie(),
						Email: userEmail
					},
					crossDomain: true
				};

				return ajax(request).pipe(
					mergeMap((): any => {
						return from([
							UserActions.updateUserSubscribeSuccess({
								title: 'Success!',
								message: 'You have been successfully subscribed to product alert emails.',
								type: 'success',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							}),
							UserActions.updateSubscribeStatus(true)
						]);
					}),
					catchError(error =>
						of(
							UserActions.updateUserSubscribeError({
								title: 'Changes not Saved',
								message: 'Your account settings could not be updated, please try again.',
								type: 'error',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							})
						)
					)
				);
			})
		);
	};

	static updateUserUnsubscribe = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_USER_UNSUBSCRIBE),
			mergeMap(_action => {
				const { payload } = _action;
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const request = {
					method: 'PUT',
					url: `${import.meta.env.VITE_BEACON_API_URL}/profile/unsubscribe-newsletter/${
						payload.email
					}`,
					headers: {
						'Content-Type': 'application/json',
						Authorization: getIirisCookie(),
						Email: userEmail
					},
					crossDomain: true,
					body: {
						reason: payload.reason
					}
				};

				return ajax(request).pipe(
					mergeMap((): any => {
						return of(
							UserActions.updateUserProfileEpic({
								title: 'Feedback Sent',
								message: 'Thank you for letting us know! We appreciate your input.',
								type: 'success',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							})
						);
					}),
					catchError(error =>
						of(
							UserActions.userProfileError({
								title: 'Changes not Saved',
								message: 'Your account settings could not be updated, please try again.',
								type: 'error',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							})
						)
					)
				);
			})
		);
	};

	static getUserProfileEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_USER_PROFILE),
			mergeMap(_action => {
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const request = {
					method: 'POST',
					url: `${import.meta.env.VITE_IRIS_PROFILE_END_POINT}`,
					headers: {
						'Content-Type': 'application/json',
						Authorization: getIirisCookie()
					},
					crossDomain: true,
					body: {
						email: userEmail,
						profile_type: 'business_email'
					}
				};
				return ajax(request).pipe(
					mergeMap((result): any => {
						const { response }: any = result;
						const userProfile = response?.status === 'success' ? response?.data?.[0] : null;
						if (response?.status === 'success') {
							return from([
								UserActions.getUserProfileSuccess(userProfile),
								{ type: UserActions.GET_USER_PROFILE_BEACON }
							]);
						} else {
							return of({ type: AppActions.UNAUTHORIZED_ERROR });
						}
					}),
					catchError(() => of({ type: AppActions.UNAUTHORIZED_ERROR }))
				);
			})
		);
	};

	static getAudienceRegistration = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_AUDIENCE_REGISTRATION),
			mergeMap(_action => {
				// const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const productId = import.meta.env.VITE_PASSPORT_CONFIG;
				// const audienceId = getLocalStorageValue(IirisIdentityKey.USER_ID);
				const aid = getCookieValue('IRIS_ID');
				const request = {
					method: 'GET',
					url: `${
						import.meta.env.VITE_IIRIS_FETCH_AUDIENCE_REGISTRATION_ENDPOINT
					}${aid}/${productId}`,
					headers: {
						'Content-Type': 'application/json',
						Authorization: getIirisCookie()
					},
					crossDomain: true
				};
				return ajax(request).pipe(
					mergeMap((result): any => {
						const { response }: any = result;
						const userProfile = response?.status === 'success' ? response?.data?.[0] : null;
						if (response?.status === 'success') {
							const trackingPayload: ITrackingEvent = {
								transactionId: uuidv4(),
								entityId: getLocalStorageValue(IirisIdentityKey.USER_ID)!,
								status: TrackingEventStatus.SUCCESS,
								type: TrackingEventType.COMPANY_DETAILS,
								timeStamp: new Date().toISOString(),
								eventPayload: {
									id: getLocalStorageValue(IirisIdentityKey.USER_ID)!,
									name: userProfile?.company,
									type: userProfile?.job_title,
									url: window.location.href,
									tags: [],
									metadata: {
										companyName: userProfile?.[allQuestionsIds[0]],
										webSite: userProfile?.[allQuestionsIds[1]],
										position: userProfile?.[allQuestionsIds[2]],
										// companyName: allQuestionsIds[0],
										// webSite: allQuestionsIds[1],
										// position: allQuestionsIds[2],
										// jobType:
										// 	userProfile?.job_functions.length > 0
										// 		? userProfile?.job_functions?.[0]?.id
										// 		: undefined,
										jobType: userProfile?.[allQuestionsIds[3]],
										enviroment: import.meta.env.VITE_ENV_NAME
									}
								}
							};
							return from([
								UserActions.getUserProfileSuccess(userProfile),
								UserActions.updateUserProfile({
									companyDetails: {
										name: userProfile?.[allQuestionsIds[0]],
										website: userProfile?.[allQuestionsIds[1]],
										userType: userProfile?.[allQuestionsIds[2]],
										// name: allQuestionsIds[0],
										// website: allQuestionsIds[1],
										// userType: allQuestionsIds[2],
										isEditing: false,
										// jobType:
										// 	userProfile?.job_functions.length > 0
										// 		? userProfile?.job_functions?.[0]?.id
										// 		: undefined
										jobType: userProfile?.[allQuestionsIds[3]]
									},
									trackingPayload
								}),
								{ type: UserActions.GET_USER_PROFILE }
							]);
						} else {
							return of({ type: AppActions.UNAUTHORIZED_ERROR });
						}
					}),
					catchError(error => {
						console.log('result error', error);
						return of({ type: AppActions.UNAUTHORIZED_ERROR });
					})
				);
			})
		);
	};

	static getUserProfileBeaconLogin = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_USER_PROFILE_BEACON_LOGIN),
			mergeMap(action => {
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const { payload } = action.payload;
				if (userEmail && getIirisCookie()) {
					return from(
						apolloClient.query({
							query: GET_PROFILE,
							variables: {},
							fetchPolicy: 'no-cache'
						})
					).pipe(
						mergeMap(response => {
							if (response.data.getProfile) {
								return of(
									UserActions.updaUserBeaconInfoStatus({
										origin: payload.origin,
										status: true
									})
								);
							} else {
								return from([
									UserActions.updaUserBeaconInfoStatus({
										origin: payload.origin,
										status: false
									})
								]);
							}
						}),
						catchError(error => {
							console.error(error);
							return from([{ type: UserActions.GET_USER_PROFILE_ERROR }]);
						})
					);
				} else {
					return of({ type: AppActions.UNAUTHORIZED_ERROR });
				}
			})
		);
	};

	static getUserProfileBeaconEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_USER_PROFILE_BEACON),
			mergeMap(_action => {
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				if (userEmail && getIirisCookie()) {
					return from(
						apolloClient.query({
							query: GET_PROFILE,
							variables: {}
						})
					).pipe(
						mergeMap(response => {
							if (response.data) {
								return of(
									UserActions.getUserProfileBeaconSuccess({
										companyDetails: response.data.getProfile.companyDetails,
										ingredientsTemplateId: response.data.getProfile.ingredientsTemplateId,
										favoriteProducts: response.data.getProfile.favoriteProducts,
										isBrandAdmin: response.data.getProfile.isBrandAdmin
									})
								);
							} else {
								return of({ type: AppActions.UNAUTHORIZED_ERROR });
							}
						}),
						catchError(error => {
							return of({ type: UserActions.GET_USER_PROFILE_ERROR });
						})
					);
				} else {
					return of({ type: AppActions.UNAUTHORIZED_ERROR });
				}
			})
		);
	};
	static getAdminRole = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_USER_PROFILE_BEACON_SUCCESS),
			mergeMap(_action => {
				return from(
					apolloClient.mutate({
						mutation: GET_ROLES
					})
				).pipe(
					mergeMap(response => {
						if (response.data) {
							return of(UserActions.getUserRolesSuccess(response.data.getProfileRoles));
						} else {
							return of({ type: UserActions.GET_USER_ONBOARD_ERROR_BRAND });
						}
					}),
					catchError(error => {
						return of({ type: UserActions.GET_USER_ONBOARD_ERROR_BRAND });
					})
				);
			})
		);
	};

	static getBrandInfo = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_USER_ROLES_SUCCESS, UserActions.UPDATE_BRAND_SUCCESS),
			mergeMap(_action => {
				const isAdmin = state$.value.appState?.userState.user.isBrandAdmin;
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				if (isAdmin && userEmail && getIirisCookie()) {
					return from(
						apolloClient.mutate({
							mutation: GET_ONBOARDING_BRAND
						})
					).pipe(
						mergeMap(response => {
							if (response.data) {
								return of(UserActions.getUserBrandProfileSuccess(response.data.getBrandFromAdmin));
							} else {
								return of({ type: UserActions.GET_USER_ONBOARD_ERROR_BRAND });
							}
						}),
						catchError(error => {
							return of({ type: UserActions.GET_USER_ONBOARD_ERROR_BRAND });
						})
					);
				} else {
					return of();
				}
			})
		);
	};

	static updateBrandInfo = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_BRAND),
			mergeMap(_action => {
				const isAdmin = state$.value.appState?.userState.user.isBrandAdmin;
				const updateBrandObject = buildUpdateObjectBrand(_action.payload);
				if (isAdmin) {
					return from(
						apolloClient.mutate({
							mutation: UPDATE_BRAND,
							variables: {
								brandInfo: updateBrandObject
							}
						})
					).pipe(
						mergeMap(response => {
							if (response.data.updateBrandSalesforce.success) {
								return from([
									UserActions.updateUserBrandSuccess({
										toastMessage: {
											title: 'Success!',
											message: 'Your changes were successfully saved.',
											type: 'success',
											position: 'top-right',
											closeOnClick: false,
											autoClose: 5000
										}
									}),
									AppActions.updateProfileState({
										loading: false,
										sectionId: ''
									})
								]);
							} else {
								return from([
									UserActions.updateUserBrandError({
										title: 'Something Went Wrong!',
										message: 'Your changes were not saved, please try again later.',
										type: 'error',
										position: 'top-right',
										closeOnClick: false,
										autoClose: 5000
									}),
									AppActions.updateProfileState({
										loading: false,
										sectionId: ''
									})
								]);
							}
						}),
						catchError(error => {
							return from([
								UserActions.updateUserBrandError({
									title: 'Something Went Wrong!',
									message: 'Your changes were not saved, please try again later.',
									type: 'error',
									position: 'top-right',
									closeOnClick: false,
									autoClose: 5000
								}),
								AppActions.updateProfileState({
									loading: false,
									sectionId: ''
								})
							]);
						})
					);
				} else {
					return of();
				}
			})
		);
	};

	static getProductsInfo = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_ONBOARDING_PRODUCTS),
			mergeMap(_action => {
				return from(
					apolloClient.query({
						query: GET_ONBOARDING_PRODUCTS
					})
				).pipe(
					mergeMap(response => {
						if (response.data) {
							return of(
								UserActions.getUserProductsProfileSuccess(response.data.getProductsFromAdmin)
							);
						} else {
							return of();
						}
					}),
					catchError(_error => {
						console.log(_error);
						return of({ type: UserActions.GET_USER_ONBOARD_ERROR_PRODUCTS });
					})
				);
			})
		);
	};

	static updateProductInfo = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_PRODUCT),
			mergeMap(_action => {
				const { id } = _action.payload;
				const productUpdate = buildUpdateProduct(_action.payload);
				const isAdmin = state$.value.appState?.userState.user.isBrandAdmin;
				if (isAdmin) {
					return from(
						apolloClient.mutate({
							mutation: UPDATE_PRODUCT,
							variables: {
								productId: id,
								updateProduct: productUpdate
							}
						})
					).pipe(
						mergeMap(response => {
							if (response.data) {
								return from([
									UserActions.updateProductSuccess({
										...response.data.updateProduct,
										toastMessage: {
											title: 'Success!',
											message: 'Your changes were successfully saved.',
											type: 'success',
											position: 'top-right',
											closeOnClick: false,
											autoClose: 5000
										}
									}),
									AppActions.updateProfileState({
										loading: false,
										sectionId: ''
									})
								]);
							} else {
								return from([
									UserActions.updateProductError({
										title: 'Something Went Wrong!',
										message: 'Your changes were not saved, please try again later.',
										type: 'error',
										position: 'top-right',
										closeOnClick: false,
										autoClose: 5000
									}),
									AppActions.updateProfileState({
										loading: false,
										sectionId: ''
									})
								]);
							}
						}),
						catchError(error => {
							return from([
								UserActions.updateProductError({
									title: 'Something Went Wrong!',
									message: 'Your changes were not saved, please try again later.',
									type: 'error',
									position: 'top-right',
									closeOnClick: false,
									autoClose: 5000
								}),
								AppActions.updateProfileState({
									loading: false,
									sectionId: ''
								})
							]);
						})
					);
				} else {
					return of();
				}
			})
		);
	};

	static saveUserIngredientsTemplateIdEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.SAVE_USER_INGREDIENTS_TEMPLATE_ID),
			mergeMap(_action => {
				const { payload } = _action;
				const profileSaveIngredientMessage = 'Your Ingredient template has been updated';
				const profileSaveIngredientTitle = 'Changes Saved!';
				const onboardingSaveIngredientMessage =
					'Your ingredients template was saved and only matching products will be shown.';
				const onboardingSaveIngredientTitle = 'Template Saved!';
				const isOnProfilePage = window.location.pathname.includes('profile');
				return from(
					apolloClient.mutate({
						mutation: UPDATE_PROFILE,
						variables: {
							id: getLocalStorageValue(IirisIdentityKey.USER_ID),
							ingredientsTemplateId: payload
								? payload.toString()
								: state$?.value?.appState?.userState?.ingredientsTemplateId.toString(),
							email: getLocalStorageValue(IirisIdentityKey.USER_EMAIL)
						}
					})
				).pipe(
					mergeMap(response => {
						return of(
							UserActions.saveIngredientTemplateSuccess({
								message: isOnProfilePage
									? profileSaveIngredientMessage
									: onboardingSaveIngredientMessage,
								title: isOnProfilePage ? profileSaveIngredientTitle : onboardingSaveIngredientTitle,
								type: 'success',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000,
								ingredientsTemplateId: response.data.updateProfile.ingredientsTemplateId
							})
						);
					}),
					catchError(() =>
						of(
							UserActions.saveIngredientTemplateError({
								message: 'Your ingredients template could not be updated, please try again.',
								type: 'error',
								title: 'Changes not Saved',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							})
						)
					)
				);
			})
		);
	};

	static updateUserCompanyDetailsEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_USER_PROFILE),
			mergeMap(_action => {
				const { companyDetails, trackingPayload, userProfile } = _action.payload;
				const { isEditing, ...rest } = companyDetails;
				const email = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const ingredientsTemplateId = isEditing
					? state$?.value?.appState?.userState?.ingredientsTemplateId.toString()
					: '1';
				const isOnProfilePage = window.location.pathname.includes('profile');
				return from(
					apolloClient.mutate({
						mutation: UPDATE_PROFILE,
						variables: {
							id: getLocalStorageValue(IirisIdentityKey.USER_ID),
							ingredientsTemplateId,
							email,
							companyDetails: rest
						}
					})
				).pipe(
					mergeMap(response => {
						if (isOnProfilePage) {
							const updateIrisUserProfile = new Promise((resolve, reject) => {
								window.IIRISProfileMgr.setIIRISProfile(userProfile).then(({ data }: any) => {
									resolve(data[0]);
								});
							});
							const updateIrisUserProfile$ = from(updateIrisUserProfile);

							return from(
								updateIrisUserProfile$.pipe(
									mergeMap((data: any) => {
										return of(
											UserActions.updateUserProfileEpic({
												title: 'Changes Saved!',
												message: 'Your account settings have been updated.',
												type: 'success',
												position: 'top-right',
												closeOnClick: false,
												autoClose: 5000,
												companyDetails: response.data.updateProfile.companyDetails,
												ingredientsTemplateId: response.data.updateProfile.ingredientsTemplateId,
												favoriteProducts: response.data.updateProfile.favoriteProducts,
												userProfile: data
											})
										);
									})
								)
							);
						} else {
							trackingPayload && handleTracking(trackingPayload);
							return of(
								UserActions.updateCompanyDetailsSuccess({
									companyDetails: response.data.updateProfile.companyDetails
								})
							);
						}
					}),
					catchError(error =>
						of(
							UserActions.userProfileError({
								title: 'Changes not Saved',
								message: 'Your account settings could not be updated, please try again.',
								type: 'error',
								position: 'top-right',
								closeOnClick: false,
								autoClose: 5000
							})
						)
					)
				);
			})
		);
	};

	static removeAllFavoriteProducts = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.DELETE_ALL_FAVORITE),
			mergeMap(() => {
				const errorMessageRemoveFavorites: IToastState = {
					title: 'Changes not Saved',
					message: 'Your favorites could not be removed, please try again.',
					type: 'error',
					position: 'top-right',
					closeOnClick: false,
					autoClose: 5000
				};
				const id = state$?.value?.appState?.userState?.user?.id;
				return from(
					apolloClient.mutate({
						mutation: DELETE_ALL_FAVORITE,
						variables: {
							profileId: id
						}
					})
				).pipe(
					mergeMap(response => {
						if (!response?.data?.removeAllFavoriteProducts) {
							return throwError(() => new Error('Something went wrong!'));
						}
						return from([
							{ type: UserActions.DELETE_ALL_FAVORITE_SUCCESS },
							UserActions.deteleAllFavorites,
							AppActions.setModalContent(undefined)
						]);
					}),
					catchError(error => of(UserActions.userProfileError(errorMessageRemoveFavorites)))
				);
			})
		);
	};

	static updateUserFavoriteProductsEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.UPDATE_USER_FAVORITE_PRODUCTS),
			mergeMap(action => {
				const currentFavorites = state$?.value?.appState?.userState?.favoriteProducts;
				const currentEvent = state$?.value?.appState?.storeState?.getCurrentEvent?.id;
				const currentFavoritesIds = currentFavorites?.map((favorite: any) => favorite?.pid);
				const { trackingEvent, productId, showExpoLabel } = action.payload;
				const isAdding = !currentFavoritesIds?.includes(productId);
				const errorMessageOnUpdateFavorites: IToastState = {
					title: 'Changes not Saved',
					message: 'Your favorites could not be updated, please try again.',
					type: 'error',
					position: 'top-right',
					closeOnClick: false,
					autoClose: 5000
				};
				return from(
					apolloClient.mutate({
						mutation: isAdding ? ADD_FAVORITE : DELETE_FAVORITE,
						variables: {
							id: getLocalStorageValue(IirisIdentityKey.USER_ID),
							productId: productId,
							...(showExpoLabel && { expoEventId: currentEvent })
						}
					})
				).pipe(
					mergeMap(response => {
						if (response && trackingEvent && isAdding) {
							handleTracking(trackingEvent);
						}
						let favoriteProducts = isAdding
							? response.data.addFavoriteProduct.favoriteProducts
							: response.data.deleteFavoriteProduct.favoriteProducts;
						return from([
							UserActions.setFavoriteProducts(favoriteProducts, undefined),
							{ type: UserActions.GET_USER_FAVORITE_PRODUCTS_SUCCESS }
						]);
					}),
					catchError(error => of(UserActions.userProfileError(errorMessageOnUpdateFavorites)))
				);
			})
		);
	};

	static downloadFavoriteProductsEpic = (
		action$: Observable<any>,
		state$: StateObservable<any>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.DOWNLOAD_FAVORITE_PRODUCTS),
			mergeMap(action => {
				const userId = getLocalStorageValue(IirisIdentityKey.USER_ID);
				const onErrorToastMessage: IToastState = {
					title: 'Error in Download',
					message: 'CSV download unsuccessful, please try again.',
					type: 'error',
					position: 'top-right',
					closeOnClick: false,
					autoClose: 5000
				};
				const userEmail = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);

				if (userEmail && getIirisCookie()) {
					const headers = {
						email: userEmail,
						authorization: getIirisCookie() || ''
					};
					const url = `${import.meta.env.VITE_BEACON_API_URL}/profile/${userId}/favorites`;

					return from(fetch(url, { headers })).pipe(
						mergeMap(response =>
							response.blob().then(blob => {
								if (response.status === 200) {
									const a = document.createElement('a');
									a.download = 'Beacon Discovery Product Favorites';
									a.href = window.URL.createObjectURL(blob);
									const clickEvt = new MouseEvent('click', {
										view: window,
										bubbles: true,
										cancelable: true
									});
									a.dispatchEvent(clickEvt);
									a.remove();
									return of(
										UserActions.downloadFavoriteProductsSuccess({
											title: 'CSV Downloaded',
											message: 'Export successful.',
											type: 'success',
											position: 'top-right',
											closeOnClick: false,
											autoClose: 5000
										})
									);
								} else {
									return of(UserActions.downloadFavoriteProductsError(onErrorToastMessage));
								}
							})
						),
						catchError(err => of(UserActions.downloadFavoriteProductsError(onErrorToastMessage)))
					);
				} else {
					return of(UserActions.downloadFavoriteProductsError(onErrorToastMessage));
				}
			})
		);
	};
	static createUserProfileBeacon = (action$: Observable<any>): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.CREATE_USER_PROFILE_BEACON),
			mergeMap(action => {
				const { trackingEvent, popup } = action.payload;
				const toastMessageOnCreateProfile: IToastState = {
					title: 'Error during profile creation',
					message: 'Your Beacon account could not be created, please try again.',
					type: 'error',
					position: 'top-right',
					closeOnClick: false,
					autoClose: 5000
				};
				const email = getLocalStorageValue(IirisIdentityKey.USER_EMAIL);
				const token = getIirisCookie();
				const request = {
					url: `${import.meta.env.VITE_IRIS_PROFILE_END_POINT}`,
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: token
					},
					crossDomain: true,
					body: {
						email: email,
						profile_type: 'business_email'
					}
				};
				return ajax(request).pipe(
					mergeMap((result): any => {
						const { response }: any = result;
						const userProfile = response?.status === 'success' ? response?.data?.[0] : null;
						if (response?.status === 'success') {
							return from(
								apolloClient.mutate({
									mutation: CREATE_PROFILE,
									variables: { id: userProfile.id, email: email, ingredientsTemplateId: '1' }
								})
							).pipe(
								mergeMap(response => {
									setLocalStorageValue(IirisIdentityKey.USER_ID, response.data.createProfile.id);
									if (trackingEvent) {
										const event = {
											...trackingEvent,
											entityId: response.data.createProfile.id,
											eventPayload: {
												...trackingEvent.eventPayload,
												id: response.data.createProfile.id,
												name: response.data.createProfile.id,
												metadata: {
													...trackingEvent.eventPayload.metadata,
													user_id: response.data.createProfile.id
												}
											}
										};

										handleTracking(event);
									}
									return from([
										AppActions.setModalContent(popup),
										UserActions.updaUserBeaconInfoStatus({
											origin: undefined,
											status: undefined
										})
									]);
								}),
								catchError(error => {
									console.error(error);
									return of(UserActions.setCreateBeaconProfileError(toastMessageOnCreateProfile));
								})
							);
						}
					}),
					catchError(error => {
						console.error(error);
						return of(UserActions.setCreateBeaconProfileError(toastMessageOnCreateProfile));
					})
				);
			})
		);
	};
	static setCreateUserProfileErrorEpic = (action$: Observable<any>): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.SET_CREATE_BEACON_PROFILE_ERROR),
			mergeMap(action => {
				removeAllAuthenticationKeysFromLocalStorage();
				return of(UserActions.createBeaconProfileError(action.payload));
			})
		);
	};
	static getAnonymousUserTokenEpic = (action$: Observable<any>): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.GET_ANONYMOUS_USER_TOKEN),
			mergeMap(action => {
				const request = {
					url: `${import.meta.env.VITE_BEACON_API_URL}/public/authorize`,
					method: 'POST',
					crossDomain: true
				};
				return ajax(request).pipe(
					mergeMap(({ response }: any) => {
						setLocalStorageValue(IirisIdentityKey.USER_IDENTITY, response?.token);
						return of({ type: UserActions.GET_ANONYMOUS_USER_TOKEN_SUCCESS });
					}),
					catchError(() => {
						removeAllAuthenticationKeysFromLocalStorage();
						return of({ type: UserActions.GET_ANONYMOUS_USER_TOKEN_ERROR });
					})
				);
			})
		);
	};
	static registerUserClickEventEpic = (
		action$: Observable<any>,
		state$: StateObservable<RootState>
	): Observable<any> => {
		return action$.pipe(
			ofType(UserActions.REGISTER_USER_CLICK_EVENT),
			mergeMap(action => {
				const token = getIirisCookie();
				const request = {
					url: `${import.meta.env.VITE_BEACON_API_URL}/public/registerEvent`,
					method: 'POST',
					crossDomain: true,
					headers: {
						'Content-Type': 'application/json',
						Authorization: token
					},
					body: {
						type: action.payload
					}
				};
				return ajax(request).pipe(
					mergeMap(({ response }: any) => {
						if (response.status === 'EXPIRED') {
							localStorage.setItem(
								lastUrlLocalStorageKey,
								window.location.pathname + window.location.search + window.location.hash
							);
							return of({ type: UserActions.REGISTER_USER_CLICK_EVENT_ERROR });
						}
						return of({ type: UserActions.REGISTER_USER_CLICK_EVENT_SUCCESS });
					}),
					catchError(error => {
						console.error(error);
						if (error.status === 401 || error.status === 403) {
							localStorage.setItem(
								lastUrlLocalStorageKey,
								window.location.pathname + window.location.search + window.location.hash
							);
							return of({ type: UserActions.REGISTER_USER_CLICK_EVENT_ERROR });
						} else {
							return of({ type: 'END_STATE', payload: action.payload });
						}
					})
				);
			})
		);
	};
}
